import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

const SCRIPT_ID = "cf-turnstile-script";
const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

function loadTurnstileScript() {
	if (typeof window === "undefined") {
		return Promise.reject(new Error("Turnstile requires a browser"));
	}

	if (window.turnstile) {
		return Promise.resolve(window.turnstile);
	}

	const existing = document.getElementById(SCRIPT_ID);
	if (existing?.dataset.failed === "true") {
		existing.remove();
		return loadTurnstileScript();
	}
	if (existing) {
		return new Promise((resolve, reject) => {
			existing.addEventListener("load", () => resolve(window.turnstile));
			existing.addEventListener("error", () => reject(new Error("Turnstile script failed to load")));
		});
	}

	return new Promise((resolve, reject) => {
		const script = document.createElement("script");
		script.id = SCRIPT_ID;
		script.src = SCRIPT_SRC;
		script.async = true;
		script.onload = () => resolve(window.turnstile);
		script.onerror = () => {
			script.dataset.failed = "true";
			reject(new Error("Turnstile script failed to load"));
		};
		document.head.appendChild(script);
	});
}

/**
 * Cloudflare Turnstile widget for auth forms.
 * Exposes reset() via ref so callers can clear the token after a failed submit.
 */
const AuthRecaptcha = forwardRef(function AuthRecaptcha({ onChange }, ref) {
	const containerRef = useRef(null);
	const widgetIdRef = useRef(null);
	const onChangeRef = useRef(onChange);
	const [size, setSize] = useState(null);
	const [status, setStatus] = useState("loading");
	const [attempt, setAttempt] = useState(0);

	useEffect(() => {
		const measure = () => {
			const width = containerRef.current?.getBoundingClientRect().width;
			setSize(width > 0 && width < 300 ? "compact" : "flexible");
		};
		measure();
		if (typeof ResizeObserver === "undefined") return;
		const observer = new ResizeObserver(measure);
		observer.observe(containerRef.current);
		return () => observer.disconnect();
	}, []);

	useEffect(() => {
		onChangeRef.current = onChange;
	}, [onChange]);

	useImperativeHandle(ref, () => ({
		reset() {
			if (widgetIdRef.current != null && window.turnstile) {
				window.turnstile.reset(widgetIdRef.current);
			}
			onChangeRef.current?.("");
		},
	}));

	useEffect(() => {
		if (!size) return;
		let cancelled = false;
		setStatus("loading");
		onChangeRef.current?.("");
		const fail = () => {
			if (cancelled) return;
			setStatus("error");
			onChangeRef.current?.("");
		};

		loadTurnstileScript()
			.then((turnstile) => {
				if (cancelled || !containerRef.current) return;
				if (!turnstile) throw new Error("Security check unavailable");

				widgetIdRef.current = turnstile.render(containerRef.current, {
					sitekey: process.env.REACT_APP_CLOUDFLARE_CHALLENGE_SITE_KEY,
					theme: "light",
					size,
					callback: (token) => {
						if (cancelled) return;
						setStatus("ready");
						onChangeRef.current?.(token || "");
					},
					"expired-callback": () => !cancelled && onChangeRef.current?.(""),
					"error-callback": fail,
					"timeout-callback": fail,
				});
				setStatus((current) => current === "error" ? current : "ready");
			})
			.catch(fail);

		return () => {
			cancelled = true;
			if (widgetIdRef.current != null && window.turnstile) {
				window.turnstile.remove(widgetIdRef.current);
				widgetIdRef.current = null;
			}
		};
	}, [size, attempt]);

	return (
		<div className="auth-recaptcha w-full overflow-hidden rounded-xl">
			<div ref={containerRef} />
			{status === "loading" && <p className="auth-recaptcha__status" role="status">Loading security check…</p>}
			{status === "error" && (
				<div className="auth-recaptcha__status" role="alert">
					<p>The security check couldn't load.</p>
					<button type="button" className="auth-text-link" onClick={() => setAttempt((value) => value + 1)}>Try again</button>
				</div>
			)}
		</div>
	);
});

export default AuthRecaptcha;
